import ShowPriceSettingSwitch from "@dana/Components/DanaMenu/ShowPriceSettingSwitch";
import UserIcon from "@dana/Icons/UserIcon";
import { MappedLink } from "@dana/MobiusOverrides/Menu/Menu";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import colorPallete, { buttonFonts } from "@dana/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPagesByParent } from "@insite/client-framework/Services/ContentService";
import translate from "@insite/client-framework/Translate";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Modal from "@insite/mobius/Modal";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type DanaPanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        currentParentTitle?: string;
        parentUrl?: string;
        parentCategoryName?: string;
        parentCategoryUrl?: string;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentLanguageId?: any;
        defaultLanguageId?: any;
        parentNodeId?: string;
        menuLinks?: MappedLink[] | undefined;
    }
>;

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement<{ id: string; onClick: () => void; closeOverlay: () => void }>;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        handleOpenScoreCardModal?: (modalType: string) => void;
        handleCoreReturns?: () => void;
        handleFindMyRep?: () => void;
        handleDownloadLeadTimes?: () => void;
        handleDownloadPriceList?: (isFuture: boolean) => void;
        handleApiKey?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        /** Allow to open parent links */
        allowOpenParentLinks?: boolean;
        /**
         * Delays rendering children until the menu trigger is fired.
         * Useful for performance when rendering extremely large navigation trees.
         */
        delayRenderingChildren?: boolean;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
        border-bottom: 1px solid ${themeBackgrounds.lightGray};
    }
    &:focus > *:first-child {
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
        outline-offset: -${getProp("theme.focus.width")};
    }
    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 10%);
    overflow-y: auto;
    background: ${({ color, theme }) => resolveColor(color, theme)};

    @media (max-width: 992px) {
        height: calc(100% - 35%);
    }

    > .mobile-parent-category-link {
        padding: 8px 24px;
        background: ${themeBackgrounds.lightGray};
        border-bottom: 1px solid ${themeBackgrounds.lightGray};
        &.industries {
        }
    }

    .view-all-title-link {
        display: flex;
        padding: 8px 16px;

        a {
            color: ${themeColors.primary.richBlack};
            text-decoration: none;
        }
    }

    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    .mobile-menu-header {
        padding-top: 0;
        padding-bottom: 0;
    }
    .close-button {
        background: transparent;
        border: none;
        justify-content: center;
        padding: 0 16px;
        align-items: center;
        [class*="IconWrapper"] {
            color: ${themeBackgrounds.darkGray};
            height: 16px;
            width: 16px;
            svg {
                height: 16px;
                width: 16px;

                path {
                    fill: ${themeColors.primary.richBlack};
                }
            }
            &:hover {
                height: 16px;
                width: 16px;
            }
        }
        @media only screen and (max-width: 768px) {
            padding: 0 16px;
        }
    }
    .back-button {
        justify-content: center;
        align-items: center;
        padding: 8px 16px;

        [class*="IconWrapper"] {
            color: ${themeBackgrounds.darkGray};
            height: 16px;
            width: 16px;
            svg {
                height: 16px;
                width: 16px;

                path {
                    fill: ${themeColors.primary.richBlack};
                }
            }
            &:hover {
                height: 16px;
                width: 16px;
            }
        }
        &:hover {
            background: transparent;
        }
        display: flex;
        align-items: center;

        @media only screen and (max-width: 768px) {
            padding: 8px 16px;
        }

        border: none;

        span {
            color: ${themeColors.primary.richBlack};
            font-size: ${buttonFonts.buttonSm.fontSize};
            line-height: ${buttonFonts.buttonSm.lineHeight};
            font-weight: ${buttonFonts.buttonSm.fontWeight};
            padding-top: 1px;

            &:nth-child(2) {
                padding-top: 2px;
            }
        }
    }
    ${injectCss}
`;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = DanaPanelMenuComponentProps & PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        menuLinks: this.props.menuItems,
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        // eslint-disable-line camelcase
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => { });
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };
    componentDidMount() {
        if (this.props.menuItems) {
            let customLinks: MappedLink[] | undefined;
            customLinks = { ...this.props.menuItems };
            const languageKey = this.props.currentLanguageId ?? this.props.defaultLanguageId;
            customLinks = this.updateCustomPageTitle(this.props.menuItems, languageKey);

            this.setState({ menuLinks: customLinks });
        }
    }

    updateCustomPageTitle = (staticPagelink: MappedLink[], languageId: any) => {
        let childOverrideTitle: MappedLink[] = [];
        if (staticPagelink) {
            if (this.props.parentNodeId) {
                const tempParameter = { parentNodeId: this.props.parentNodeId };
                let dsgCustomPages: PageModel[];
                getPagesByParent(tempParameter).then(result => {
                    dsgCustomPages = result?.pages;
                    if (dsgCustomPages) {
                        staticPagelink.forEach(child => {
                            if (child.type === "DsgCustomPage" && child.url) {
                                let childLink: MappedLink | undefined;
                                childLink = { ...child };
                                let page;
                                if (dsgCustomPages && dsgCustomPages.filter(x => x.nodeId === child.id)) {
                                    page = dsgCustomPages.filter(x => x.nodeId === child.id)[0];
                                }

                                if (
                                    page &&
                                    page.translatableFields &&
                                    page?.translatableFields["overrideTitle"] &&
                                    page?.translatableFields["overrideTitle"][languageId] &&
                                    childLink
                                ) {
                                    childLink.title = page?.translatableFields["overrideTitle"][languageId];
                                    childOverrideTitle.push(childLink);
                                } else {
                                    childOverrideTitle.push(child);
                                }
                            } else {
                                childOverrideTitle.push(child);
                            }
                        });
                        return childOverrideTitle;
                    }
                });
            } else {
                childOverrideTitle = staticPagelink;
            }
        }
        return childOverrideTitle;
    };

    renderMenuItems(
        childMenuItems: MappedLink[],
        currentDepth: number,
        propBuilder: any,
        parentTitle: string,
        parentUrl: string,
    ) {
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");

        const currentUrlRegex = new RegExp(currentUrl!, "gi");
        return (
            <>
                <PanelRow
                    key={this.props.parentCategoryUrl}
                    tabIndex={this.state.layer === 0 ? 0 : -1}
                    css={css`
                        width: 100%;
                        text-transform: capitlize;
                        padding-top: 0px;
                    `}
                    {...panelRowProps}
                >
                    <Typography
                        className="sample header-sample text-body-md"
                        data-test-selector={`${this.props["data-test-selector"]}_${this.props.parentCategoryUrl}`}
                        {...typographyProps}
                    >
                        {parentTitle && parentTitle !== "" ? parentTitle : this.props.parentCategoryName}
                    </Typography>
                </PanelRow>

                {parentUrl &&
                    !(
                        parentTitle === "Settings" ||
                        parentTitle === "Customer Scorecard" ||
                        parentTitle === "Returns" ||
                        parentTitle === "Download Price List" ||
                        parentTitle === "API Developer" ||
                        parentTitle === "Annual Returns"
                    ) && (
                        <Typography className="view-all-title-link">
                            <a
                                href={parentUrl && parentUrl !== "" ? parentUrl : ""}
                                className="text-link text-link-primary"
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                            >
                                {translate("View All ")}
                                {parentTitle && parentTitle !== "" ? parentTitle : ""}
                            </a>
                        </Typography>
                    )}

                {childMenuItems
                    .filter(item => !item.excludeFromNavigation)
                    .map(item => {
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth;
                        const clickableProps: { href?: string, target?: string } = { ...item.clickableProps };
                        if (!renderChildren && item.url) {
                            clickableProps.href = item.url;
                        }
                        if (item?.openInNewWindow) {
                            clickableProps.target = "_blank";
                        }

                        const menuItem =
                            item.title === "PriceToggle" ? (
                                <Typography
                                    key={item.title}
                                    className="sample text-body-md "
                                    data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                    {...typographyProps}
                                >
                                    <ShowPriceSettingSwitch></ShowPriceSettingSwitch>
                                </Typography>
                            ) : (
                                <PanelRow
                                    key={item.title}
                                    isCurrent={item.url?.match(currentUrlRegex) || item.title?.match(currentUrlRegex)}
                                    onClick={(e: React.MouseEvent) => {

                                        if (item?.url === "/MyAccount/SingleAccount") {
                                            e.preventDefault();

                                            this.props.handleOpenScoreCardModal?.("Single Account");
                                        } else if (item?.url === "/MyAccount/BuyingGroup") {
                                            e.preventDefault();

                                            this.props.handleOpenScoreCardModal?.("Buying Group");
                                        } else if (item?.url === "/MyAccount/CoreReturn") {
                                            e.preventDefault();

                                            this.props.handleCoreReturns?.();
                                        } else if (item?.url === "/MyAccount/FindMyRepresentative") {
                                            e.preventDefault();
                                            this.props.handleFindMyRep?.();
                                        } else if (item?.url === "/MyAccount/DownloadLeadTimes") {
                                            e.preventDefault();
                                            this.props.handleDownloadLeadTimes?.();
                                        } else if (item?.url === "/MyAccount/DownloadPriceList/CurrentPriceList") {
                                            e.preventDefault();
                                            this.props.handleDownloadPriceList?.(false);
                                        } else if (item?.url === "/MyAccount/DownloadPriceList/FuturePriceList") {
                                            e.preventDefault();
                                            this.props.handleDownloadPriceList?.(true);
                                        } else if (item?.url === "/MyAccount/APIDeveloper/apikey") {
                                            e.preventDefault();

                                            this.props.handleApiKey?.();
                                        }
                                        if (this.props.closeOverlay) {
                                            this.props.closeOverlay();
                                        }
                                    }}
                                    color="white"
                                    hasChildren={renderChildren || false}
                                    tabIndex={this.state.layer === 0 ? 0 : -1}
                                    css={css`
                                        width: 100%;
                                        text-transform: capitlize;
                                    `}
                                    {...clickableProps}
                                    {...panelRowProps}
                                    className={
                                        item.url?.match(currentUrlRegex) || item.title?.match(currentUrlRegex)
                                            ? "active"
                                            : "inActive"
                                    }
                                >
                                    <Typography
                                        className="sample text-body-md "
                                        data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                        {...typographyProps}
                                    >
                                        {item.title}
                                    </Typography>
                                </PanelRow>
                            );
                        if (renderChildren) {
                            return (
                                <PanelMenu
                                    {...this.props}
                                    as="div"
                                    key={item.title}
                                    currentParentTitle={item.title || parentTitle}
                                    parentUrl={item.url || parentUrl}
                                    panelTrigger={menuItem}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                />
                            );
                        }
                        return menuItem;
                    })}
            </>
        );
    }

    render() {
        return (
            <>
                <ThemeConsumer>
                    {theme => {
                        const {
                            currentUrl,
                            descriptionId,
                            menuItems,
                            panelTrigger,
                            closeOverlay,
                            mergeCss,
                            currentParentTitle,
                            parentUrl,
                            ...otherProps
                        } = this.props;
                        const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                        const { translate } = theme;
                        const { layer, descriptionId: navId } = this.state;
                        const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                        const headerColor = applyProp("headerColor", "common.backgroundContrast");
                        return (
                            <PanelMenuWrapper
                                css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                {...{ "aria-labelledby": navId }}
                                {...otherProps}
                                data-layer={`layer-${layer}`}
                            >
                                {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                                <PanelChildren
                                    displayLayer={layer}
                                    aria-hidden={layer > 0}
                                    css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                    data-layer={layer}
                                    tabIndex={layer === 0 ? 0 : -1}
                                    color={themeColors.primary.white}
                                >
                                    <PanelRow
                                        as="div"
                                        header
                                        color={themeColors.primary.white}
                                        className="mobile-menu-header"
                                    >
                                        <Button
                                            color="none"
                                            className="back-button text-body-md"
                                            onClick={this.moveNextRight}
                                            tabIndex={layer === 0 ? 0 : -1}
                                        >
                                            <ButtonIcon
                                                {...spreadProps("backIconProps")}
                                                color={themeColors.primary.black}
                                                size={20}
                                                className="text-body-md"
                                            />
                                            <span className="text-body-md">{translate("Back")}</span>
                                        </Button>
                                        {closeOverlay && (
                                            <Button
                                                onClick={closeOverlay}
                                                tabIndex={layer === 0 ? 0 : -1}
                                                className="close-button text-body-md"
                                            >
                                                <ButtonIcon
                                                    {...spreadProps("closeIconProps")}
                                                    className="text-body-md"
                                                />
                                                <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                                            </Button>
                                        )}
                                    </PanelRow>
                                    <PanelMenuStyle
                                        css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                        color={themeColors.primary.white}
                                    >
                                        {this.state.menuLinks ? (
                                            this.renderMenuItems(
                                                this.state.menuLinks!,
                                                1,
                                                propBuilder,
                                                currentParentTitle as string,
                                                parentUrl as string,
                                            )
                                        ) : (
                                            <>{this.props.children}</>
                                        )}
                                    </PanelMenuStyle>
                                </PanelChildren>
                            </PanelMenuWrapper>
                        );
                    }}
                </ThemeConsumer>
            </>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);
